/**
 * Chat bubble labeling for Theo (neo), agent-owned ticket runs, and human technicians.
 *
 * A technician's own chat labels neo replies "Theo Assistant". When an agent is working the
 * ticket, Horizon stamps `agent_display_name` on that run's messages and both the kickoff
 * and the replies use the agent's name. Canonical automation actor matches Horizon
 * AUTO_MODE_ACTOR_USER_ID (`autoagent`); legacy `system` rows may still appear in older threads.
 */

export const ASSISTANT_USER_ID = "neo";

export const THEO_ASSISTANT_DISPLAY_NAME = "Theo Assistant";

export const AUTOMATION_ACTOR_PRIMARY_ID = "autoagent";

export const AUTOMATION_AGENT_DISPLAY_NAME = "Auto Agent";

const AUTOMATION_ACTOR_IDS = new Set<string>([
  AUTOMATION_ACTOR_PRIMARY_ID,
  AUTOMATION_ACTOR_PRIMARY_ID.toUpperCase(),
  "system",
  "SYSTEM",
]);

export interface CurrentUserForBubble {
  name?: string | null;
  email?: string | null;
  avatar?: string | null;
}

export interface ChatBubblePresentation {
  chatBubbleSender: "user" | "assistant";
  senderName: string;
  /** Passed to ChatBubble `userId` (header / accessibility). */
  userIdLabel: string;
  /** Optional avatar for technician user lane only. */
  userAvatar?: string;
}

/** True when Cortex/Horizon stored user_id is automation actor (modern or legacy). */
export function isAutomationActorUserId(userId: string | null | undefined): boolean {
  if (userId == null || userId === "") return false;
  return AUTOMATION_ACTOR_IDS.has(String(userId).trim());
}

export function isNeoAssistantAuthor(userId: string | null | undefined): boolean {
  return String(userId || "").trim() === ASSISTANT_USER_ID;
}

/**
 * Technician-authored message vs automation vs assistant.
 * Used for report-attachment/header logic: treat neo as assistant lane; technician + automation as user-lane bodies.
 */
export function isDisplayedAsUserLaneBody(
  messageUserId: string | null | undefined,
  loggedInHumanUserId: string | null | undefined,
): boolean {
  if (isNeoAssistantAuthor(messageUserId)) return false;
  return true;
}

/**
 * Technician match for "my" report chips (only logged-in human, not automation).
 */
export function isLoggedInHumanMessage(
  messageUserId: string | null | undefined,
  loggedInHumanUserId: string | null | undefined,
): boolean {
  if (!loggedInHumanUserId || !messageUserId) return false;
  return String(messageUserId).trim() === String(loggedInHumanUserId).trim();
}

function emailsEqual(left: string | null | undefined, right: string | null | undefined): boolean {
  const a = String(left || "").trim().toLowerCase();
  const b = String(right || "").trim().toLowerCase();
  return Boolean(a) && a === b;
}

function displayNameFromActorUserId(userId: string | null | undefined): string | null {
  const id = String(userId || "").trim();
  if (!id || isNeoAssistantAuthor(id)) return null;
  if (isAutomationActorUserId(id)) return AUTOMATION_AGENT_DISPLAY_NAME;
  if (id.includes("@")) {
    const handle = id.split("@")[0].trim();
    return handle || id;
  }
  return id;
}

export interface HumanReviewAck {
  content?: string;
  user_id?: string | null;
  user_name?: string | null;
}

export function getHumanReviewActorLabel(
  actorUserId: string | null | undefined,
  actorName: string | null | undefined,
  currentUser?: CurrentUserForBubble | null,
): string {
  const currentEmail = currentUser?.email;
  if (actorUserId && emailsEqual(actorUserId, currentEmail)) {
    return "You";
  }

  const derived = displayNameFromActorUserId(actorUserId);
  if (derived) return derived;

  const named = String(actorName || "").trim();
  if (named && !named.includes("@")) return named;

  return "You";
}

export function formatHumanReviewAck(
  ack: string | HumanReviewAck | null | undefined,
  currentUser?: CurrentUserForBubble | null,
): string {
  if (ack == null) return "";
  const content = typeof ack === "string" ? ack : String(ack.content || "");
  if (!content) return "";
  if (typeof ack === "string") return content;

  const label = getHumanReviewActorLabel(ack.user_id, ack.user_name, currentUser);
  if (label === "You") return content;
  return content.replace(/\bYou\b/g, label);
}

type AgentNamedMessage = {
  user_id?: string | null;
  agent_display_name?: string | null;
};

function trimmedAgentName(message: AgentNamedMessage | null | undefined): string | null {
  const name = String(message?.agent_display_name || "").trim();
  return name || null;
}

/** The name stamped on this message's accepted turn. */
export function agentDisplayNameForMessage(
  messages: ReadonlyArray<AgentNamedMessage>,
  index: number,
): string | null {
  const current = messages[index];
  if (!current) return null;
  const own = trimmedAgentName(current);
  const neo = isNeoAssistantAuthor(current.user_id);
  const automation = isAutomationActorUserId(current.user_id);
  if (own && (neo || automation)) return own;
  return null;
}

export function getChatBubblePresentation(
  messageUserId: string | null | undefined,
  loggedInHumanUserId: string | null | undefined,
  currentUser?: CurrentUserForBubble | null,
  /** Extension ticket chat prefers email in the bubble title when it matches technician messages. */
  preferEmailForTechnicianLabel?: boolean,
  /** Set when this message belongs to an agent run. Technician chat leaves it empty. */
  agentDisplayName?: string | null,
): ChatBubblePresentation {
  const uidRaw = messageUserId ?? "";
  const agentName = String(agentDisplayName || "").trim();

  if (isNeoAssistantAuthor(uidRaw)) {
    const senderName = agentName || THEO_ASSISTANT_DISPLAY_NAME;
    return {
      chatBubbleSender: "assistant",
      senderName,
      userIdLabel: senderName,
    };
  }

  if (isAutomationActorUserId(uidRaw)) {
    const senderName = agentName || AUTOMATION_AGENT_DISPLAY_NAME;
    return {
      chatBubbleSender: agentName ? "assistant" : "user",
      senderName,
      userIdLabel: senderName,
    };
  }

  const isSelf =
    loggedInHumanUserId != null &&
    String(uidRaw).trim() === String(loggedInHumanUserId).trim();

  const name = currentUser?.name?.trim();
  const email = currentUser?.email?.trim();

  if (isSelf) {
    const senderName =
      preferEmailForTechnicianLabel === true ? email || name || "You" : name || email || "You";
    const userIdLabel = name || email || uidRaw || "You";
    const userAvatar =
      currentUser?.avatar || (name && name[0]) || (email && email[0]) || undefined;
    return {
      chatBubbleSender: "user",
      senderName,
      userIdLabel,
      ...(userAvatar ? { userAvatar } : {}),
    };
  }

  const fallback = uidRaw.trim() || "You";
  return {
    chatBubbleSender: "user",
    senderName: fallback,
    userIdLabel: fallback,
  };
}
